import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faPhone, faEnvelope, faClock, faCheck } from '@fortawesome/free-solid-svg-icons';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);
  const [animatedElements, setAnimatedElements] = useState(new Set());

  // Animation refs
  const contactInfoRef = useRef(null);
  const mapRef = useRef(null);
  const formRef = useRef(null);
  const faqRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setFormError(true);
      setFormSubmitted(false);
      return;
    }

    try {
      // Send form data to backend
      const response = await fetch('/backend/contact-handler.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setFormSubmitted(true);
        setFormError(false);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormError(true);
      setFormSubmitted(false);
    }
  };



  // Intersection Observer for typewriter and glow animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const elementId = entry.target.id;
          setAnimatedElements(prev => new Set([...prev, elementId]));
          
          if (elementId === 'contact-info') {
            // Handle contact info cards - glow animation
            const infoCards = entry.target.querySelectorAll('.info-card');
            infoCards.forEach((card, index) => {
              card.classList.add('animate-glow');
              setTimeout(() => {
                card.classList.add('animate');
              }, index * 200); // 200ms stagger for glow effect
            });
          } else if (elementId === 'contact-map') {
            // Handle map - zoom animation
            const mapContainer = entry.target.querySelector('.map-container');
            if (mapContainer) {
              mapContainer.classList.add('animate-zoom');
              setTimeout(() => {
                mapContainer.classList.add('animate');
              }, 300);
            }
          } else if (elementId === 'contact-form') {
            // Handle form - slide from right
            const formContainer = entry.target.querySelector('.form-container');
            if (formContainer) {
              formContainer.classList.add('animate-slide-right');
              setTimeout(() => {
                formContainer.classList.add('animate');
              }, 200);
            }
          } else if (elementId === 'contact-faq') {
            // Handle FAQ section - typewriter title + fade up items
            const faqTitle = entry.target.querySelector('.section-title h2');
            if (faqTitle) {
              faqTitle.classList.add('animate-typewriter');
              setTimeout(() => {
                faqTitle.classList.add('animate');
              }, 100);
            }

            // Handle FAQ items - fade up animation
            const faqItems = entry.target.querySelectorAll('.faq-item');
            faqItems.forEach((item, index) => {
              item.classList.add('animate-fade-up');
              setTimeout(() => {
                item.classList.add('animate');
              }, index * 150 + 800); // 150ms stagger + 800ms delay after title
            });
          }
        }
      });
    }, observerOptions);

    // Observe sections
    const sections = [contactInfoRef.current, mapRef.current, formRef.current, faqRef.current];
    sections.forEach(section => {
      if (section) observer.observe(section);
    });
    
    return () => {
      observer.disconnect();
    };
  }, []); // Run once on mount

  return (
    <div className="contact-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="container">
          <h1>Contact Us</h1>
          <p>Get in touch with our general practice team or book an appointment</p>
        </div>
      </div>

      {/* Contact Information */}
      <section className="section contact-info" ref={contactInfoRef} id="contact-info">
        <div className="container">
          <div className="info-grid">
            <div className="info-card">
              <div className="info-icon">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </div>
              <h3>Our Location</h3>
              <p>Shop1 and 4, 209 Myall Street</p>
              <p>Tea Gardens, NSW 2324</p>
              <p>Australia</p>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <h3>Phone & Fax</h3>
              <p>Phone: (02) 4997 1234</p>
              <p>Fax: (02) 4997 5678</p>
              <p>Emergency: 000</p>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <h3>Email Us</h3>
              <p>info@teagardensgp.com.au</p>
              <p>appointments@teagardensgp.com.au</p>
              <p>admin@teagardensgp.com.au</p>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <h3>Opening Hours</h3>
              <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
              <p>Saturday: 9:00 AM - 1:00 PM</p>
              <p>Sunday & Public Holidays: Closed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section map-section" ref={mapRef} id="contact-map">
        <div className="container">
          <div className="map-container">
            {/* Google Maps Embed for Tea Gardens, Sydney */}
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3348.8!2d152.1654!3d-32.6654!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b7380ce304eaaab%3A0x1684be3280431968!2s1%2F209%20Myall%20St%2C%20Tea%20Gardens%20NSW%202324!5e0!3m2!1sen!2sau!4v1642000000000!5m2!1sen!2sau"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Tea Gardens Healthcare Location - 1/209 Myall Street"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section form-section" ref={formRef} id="contact-form">
        <div className="container">
          <div className="form-container">
            <div className="contact-form">
                  <h2>Send Us a Message</h2>
                  <p>Have a question or feedback? Fill out the form below and we'll get back to you as soon as possible.</p>
                  
                  {formSubmitted && (
                    <div className="form-success">
                      <FontAwesomeIcon icon={faCheck} className="success-icon" />
                      <p>Thank you for your message! We will get back to you shortly.</p>
                    </div>
                  )}

                  {formError && (
                    <div className="form-error">
                      <p>There was an error submitting your message. Please try again.</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name">Your Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="subject">Subject *</label>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="message">Your Message *</label>
                        <textarea
                          id="message"
                          name="message"
                          rows="5"
                          value={formData.message}
                          onChange={handleChange}
                          required
                        ></textarea>
                      </div>
                    </div>
                    {formSubmitted && (
                      <div className="form-message success">
                        <FontAwesomeIcon icon={faCheck} />
                        <span>Thank you for your message! We will get back to you soon.</span>
                      </div>
                    )}
                    
                    {formError && (
                      <div className="form-message error">
                        <span>Sorry, there was an error sending your message. Please try again or call us directly at 02 7241 7750.</span>
                      </div>
                    )}
                    
                    <div className="form-submit">
                      <button type="submit" className="btn-primary">Send Message</button>
                    </div>
                  </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section faq-section" ref={faqRef} id="contact-faq">
        <div className="container">
          <div className="section-title">
            <h2>Frequently Asked Questions</h2>
            <p>Find answers to common questions about our services and appointments</p>
          </div>

          <div className="faq-container">
            <div className="faq-item">
              <div className="faq-question">
                <h3>How do I make an appointment?</h3>
              </div>
              <div className="faq-answer">
                <p>You can make an appointment by calling our reception at 02 7241 7750 during our opening hours, using our online booking form on this page, or by visiting our practice in person. For urgent matters, please call us directly rather than using the online form.</p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <h3>What should I bring to my first appointment?</h3>
              </div>
              <div className="faq-answer">
                <p>Please bring your Medicare card, any healthcare cards (e.g., pension, healthcare, or DVA cards), a list of current medications, and any relevant medical records or test results. If you're transferring from another practice, it's helpful to have your previous doctor's details so we can request your medical history.</p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <h3>Do you offer telehealth consultations?</h3>
              </div>
              <div className="faq-answer">
                <p>Yes, we offer telehealth consultations for eligible patients. These can be conducted via phone or video call. Please contact our reception to determine if your concern is suitable for a telehealth appointment and to book a session.</p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <h3>What is your cancellation policy?</h3>
              </div>
              <div className="faq-answer">
                <p>We request that you provide at least 24 hours' notice if you need to cancel or reschedule your appointment. This allows us to offer the time slot to another patient. Repeated late cancellations or no-shows may incur a fee.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
